/**
 * JEDEN modal dla calej aplikacji.
 *
 * Audyt UI zastal 16 wlasnych implementacji okna modalnego (`ModalOverlay`, `ConfirmationOverlay`,
 * `EditOverlay`...). Z tych 16 tylko 2 reagowaly na Esc, 3 mialy `role="dialog"`, ZADEN nie blokowal
 * przewijania tla i zaden nie pilnowal fokusu. Do tego 27 wywolan `window.confirm()` i 58 `alert()`,
 * wiec usuniecie faktury wygladalo jak okno systemowe przegladarki, a usuniecie rezerwacji jak
 * element aplikacji. Ten plik ma byc jedynym miejscem, w ktorym te zachowania sa napisane.
 *
 * Trzy rzeczy sa tu trzymane W MODULE, a nie w stanie komponentu, i to jest celowe:
 *
 *   1. STOS OTWARTYCH MODALI — Esc musi zamykac WYLACZNIE wierzchnie okno. Kazdy modal nasluchuje
 *      na `document` (bo fokus moze byc chwilowo poza oknem), wiec bez wspolnego stosu jedno
 *      nacisniecie Esc zamykaloby wszystkie otwarte naraz.
 *   2. LICZNIK BLOKADY PRZEWIJANIA — dwa modale naraz (np. potwierdzenie nad formularzem)
 *      i zamkniecie jednego z nich nie moze odblokowac tla. Stad licznik, a nie flaga.
 *   3. POPRZEDNIA WARTOSC `overflow` — przywracamy to, co bylo, a nie na sztywno `''`. Strona,
 *      ktora sama ustawila sobie `overflow`, po zamknieciu modala dostawalaby inny uklad niz przed
 *      jego otwarciem.
 *
 * Zero nowych zaleznosci: `react-modal` jest w package.json, ale nie uzywa go nikt, a `focus-trap`
 * to kolejna paczka na 40 linii logiki, ktora i tak trzeba by dopasowac do wlasnego API.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../ui/common';

/* ── Stan wspolny dla wszystkich instancji (patrz naglowek pliku) ─────────────────────────── */

const stosModali = [];

let licznikBlokadyPrzewijania = 0;
let poprzedniOverflowBody = '';

function zablokujPrzewijanieTla() {
  licznikBlokadyPrzewijania += 1;
  if (licznikBlokadyPrzewijania > 1) return;
  poprzedniOverflowBody = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
}

function odblokujPrzewijanieTla() {
  licznikBlokadyPrzewijania = Math.max(0, licznikBlokadyPrzewijania - 1);
  if (licznikBlokadyPrzewijania > 0) return;
  document.body.style.overflow = poprzedniOverflowBody;
}

/* ── Pulapka fokusu ──────────────────────────────────────────────────────────────────────── */

const SELEKTOR_FOKUSOWALNYCH = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

/**
 * Elementy, na ktore Tab moze wejsc WEWNATRZ podanego korzenia.
 *
 * Czysta funkcja (jedyne wejscie to wezel DOM), zeby dalo sie ja sprawdzic bez renderowania —
 * w projekcie nie ma @testing-library i nie wolno jej dodawac.
 *
 * Odsiewamy trzy rzeczy, bo kazda z nich zlamalaby krazenie fokusu:
 *   - `disabled` i `type="hidden"` — Tab ich nie odwiedza, a my liczylibysmy je jako pierwszy/ostatni;
 *   - `tabindex="-1"` — element fokusowalny programowo, ale POZA kolejnoscia tabulacji (m.in. samo
 *     okno modala, ktore ma `tabIndex={-1}`, wiec bez tego filtra bylo by wlasnym pierwszym celem);
 *   - elementy ukryte (`display: none`, zwinieta sekcja) — `getClientRects()` jest tu pewniejsze niz
 *     `offsetParent`, bo ten drugi jest `null` rowniez dla elementow `position: fixed`.
 */
export function getFocusable(korzen) {
  if (!korzen || typeof korzen.querySelectorAll !== 'function') return [];
  return Array.prototype.filter.call(
    korzen.querySelectorAll(SELEKTOR_FOKUSOWALNYCH),
    (el) => {
      if (el.disabled) return false;
      if (el.getAttribute('type') === 'hidden') return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      const tabindex = el.getAttribute('tabindex');
      if (tabindex !== null && Number(tabindex) < 0) return false;
      return el.getClientRects().length > 0;
    }
  );
}

/* ── Style ───────────────────────────────────────────────────────────────────────────────── */

const SZEROKOSCI = { sm: '420px', md: '640px', lg: '900px', xl: '1200px' };

const szerokoscDla = (rozmiar) => SZEROKOSCI[rozmiar] || SZEROKOSCI.md;

/* Dwa progi „malo miejsca": waski ekran (telefon pionowo) i NISKI ekran (telefon poziomo, niskie
   okno przegladarki). Na niskim naglowek, pasek narzedzi i stopka potrafia zajac cala wysokosc
   okna i na tresc nie zostaje nic (telefon 844x390 z oknem wyboru sprzetu: lista 0 px, przycisk
   w stopce przyciety), wiec przewija sie wtedy CALE okno, a przyklejona zostaje tylko stopka. */
const NISKI_EKRAN = '(max-height: 520px)';
const TELEFON = `(max-width: 768px), ${NISKI_EKRAN}`;
/* Dla okien z `fullScreenOnMobile`, ktore ukladaja swoja tresc zwarto w tych samych warunkach. */
export const MEDIA_TELEFON = TELEFON;

/**
 * Warstwa okna dla tego, co otwiera sie Z JEGO WNETRZA w osobnym portalu (lista rozwijana, dymek).
 * Taki element ma stala warstwe --z-popover, czyli POD oknem `elevated` (--z-confirm): bez tej
 * informacji lista otwarta w podniesionym oknie chowala sie pod nim. Poza oknem wartosc to null.
 */
export const ModalLayerContext = createContext(null);
const WARSTWA_ZWYKLA = Object.freeze({ elevated: false });
const WARSTWA_PODNIESIONA = Object.freeze({ elevated: true });

const pojawienieTla = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pojawienieOkna = keyframes`
  from { opacity: 0; transform: translateY(var(--sp-3)) scale(0.98); }
  to   { opacity: 1; transform: none; }
`;

/**
 * Przyciemnienie tla to JEDYNA surowa wartosc koloru w tym pliku i jest to swiadomy wyjatek:
 * w aplikacji bylo 7 roznych przyciemnien, wiec kazde okno gasilo strone inaczej. Ujednolicone
 * na tej jednej wartosci; tokenu na to nie ma.
 */
const Tlo = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${(p) => (p.$elevated ? 'var(--z-confirm)' : 'var(--z-overlay)')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
  background: rgba(15, 23, 42, 0.55);
  animation: ${pojawienieTla} var(--dur) var(--ease);

  /* Pelny ekran: bez marginesu, ale poza wcieciem ekranu (telefon poziomo ma je z boku). */
  ${(p) => p.$fullScreenOnMobile && css`
    @media ${TELEFON} {
      padding: 0 var(--safe-right) 0 var(--safe-left);
    }
  `}
`;

const Okno = styled.div`
  position: relative;
  z-index: ${(p) => (p.$elevated ? 'var(--z-confirm)' : 'var(--z-modal)')};
  display: flex;
  flex-direction: column;
  width: min(100%, ${(p) => szerokoscDla(p.$size)});
  max-width: ${(p) => szerokoscDla(p.$size)};
  /* Dwa zapisy max-height celowo: pierwszy jest awaryjny dla przegladarek bez jednostki dvh,
     ktore odrzucilyby cala deklaracje z drugiej linii i zostawily okno bez ograniczenia wysokosci
     (czyli bez przewijania tresci). Jednostka dvh liczy sie z paskiem adresu na telefonie, vh nie.
     Uwaga na przyszlosc: w komentarzu CSS wewnatrz styled-components NIE MOZE byc znaku akcentu,
     bo konczy on szablon i psuje parsowanie calego pliku. */
  max-height: 90vh;
  max-height: min(90vh, calc(100dvh - var(--sp-8)));
  background: var(--card-bg);
  color: var(--text);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: ${pojawienieOkna} var(--dur) var(--ease);

  /* Okno dostaje fokus, gdy w srodku nie ma na co go polozyc — ale obrys wokol calego okna
     wygladalby jak blad. Fokus z klawiatury na elementach wewnatrz zostaje nietkniety. */
  &:focus {
    outline: none;
  }

  /* Stala wysokosc dla okien z filtrowana lista: bez niej okno kurczy sie i rosnie z liczba
     wynikow, a pole szukania ucieka spod kursora przy kazdym wpisanym znaku. */
  ${(p) => p.$tall && css`
    height: 85vh;
    height: min(85vh, calc(100dvh - var(--sp-8)));
  `}

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }

  ${(p) => p.$fullScreenOnMobile && css`
    @media ${TELEFON} {
      height: 100%;
      max-height: none;
      border-radius: 0;
    }
  `}

  /* Niski ekran: przewija sie cale okno, nie sama tresc (patrz NISKI_EKRAN). */
  @media ${NISKI_EKRAN} {
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const Naglowek = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-5) var(--sp-6);
  border-bottom: 1px solid var(--border);

  /* Na pelnym ekranie telefonu okno siega gornej krawedzi, wiec tytul odsuwa sie od wciecia,
     a boczny odstep maleje, zeby tresc dostala cala szerokosc. */
  ${(p) => p.$fullScreenOnMobile && css`
    @media ${TELEFON} {
      padding: calc(var(--sp-4) + var(--safe-top)) var(--sp-4) var(--sp-4);
    }
  `}
`;

const Tytuly = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Tytul = styled.h2`
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 700;
  color: var(--text);
`;

const Podtytul = styled.p`
  margin: var(--sp-1) 0 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const PrzyciskZamkniecia = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-1) var(--sp-2);
  background: transparent;
  border: none;
  border-radius: var(--r-sm);
  color: var(--text-muted);
  font-size: var(--fs-xl);
  line-height: 1;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);

  &:hover {
    background: var(--hover-bg);
    color: var(--text);
  }
`;

/* Pasek nad trescia (np. wyszukiwarka i filtry listy). Stoi poza przewijana Trescia z tego
   samego powodu co naglowek: przy dlugiej liscie filtry nie moga odjechac do gory razem z nia. */
const PasekNarzedzi = styled.div`
  flex: 0 0 auto;
  padding: var(--sp-4) var(--sp-6);
  border-bottom: 1px solid var(--border);

  ${(p) => p.$fullScreenOnMobile && css`
    @media ${TELEFON} {
      padding: var(--sp-3) var(--sp-4);
    }
  `}
`;

/* Przewija sie TRESC, nie cale okno — naglowek i stopka (czyli tytul i przyciski) maja zostac
   widoczne przy dlugiej liscie w srodku. */
const Tresc = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Bez odstepu dla tresci, ktora sama jest lista od krawedzi do krawedzi okna. */
  padding: ${(p) => (p.$flush ? '0' : 'var(--sp-6)')};
  font-size: var(--fs-base);

  @media ${NISKI_EKRAN} {
    flex: 0 0 auto;
    overflow-y: visible;
  }
`;

const Stopka = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-4) var(--sp-6);
  border-top: 1px solid var(--border);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    /* Bez tego dolny przycisk lezy pod kreska gestu na telefonie z wcieciem. */
    padding-bottom: calc(var(--sp-4) + var(--safe-bottom));

    > * {
      width: 100%;
    }
  }

  ${(p) => p.$fullScreenOnMobile && css`
    @media ${TELEFON} {
      padding-left: var(--sp-4);
      padding-right: var(--sp-4);
    }
  `}

  /* Na niskim ekranie stopka jedzie z przewijanym oknem, ale zostaje przyklejona do jego dolu.
     Margines auto dosuwa ja do dolu takze wtedy, gdy tresc jest krotsza od okna. */
  @media ${NISKI_EKRAN} {
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin-top: auto;
    background: var(--card-bg);
  }
`;

/* ── Modal ───────────────────────────────────────────────────────────────────────────────── */

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  // Nieprzewijany pasek miedzy naglowkiem a trescia — filtry dlugiej listy.
  toolbar,
  size = 'md',
  // Stala wysokosc okna (85% ekranu) zamiast dopasowania do tresci — dla filtrowanych list.
  tall = false,
  // Na telefonie (waski ALBO niski ekran) okno zajmuje caly ekran, bez marginesu i zaokraglen.
  fullScreenOnMobile = false,
  // Tresc bez wlasnego odstepu (lista od krawedzi do krawedzi okna).
  flush = false,
  // Co dostaje fokus po otwarciu. Domyslnie pierwszy element sterujacy, czyli zwykle „×” w
  // naglowku: Enter albo Spacja zaraz po otwarciu zamykaja wtedy okno (tak konczyl sie skan
  // czytnikiem kodow przy otwartym oknie wyboru sprzetu). Ref wskazuje konkretne pole, a
  // 'dialog' zostawia fokus na samym oknie, gdzie zaden klawisz niczego nie uruchamia.
  initialFocus,
  dismissOnBackdrop = true,
  closeLabel,
  'data-cy': dataCy,
  // Ponad --z-popover — dla okien, ktore MUSZA stac nad kazdym popoverem (patrz ConfirmDialog).
  elevated = false,
}) {
  const oknoRef = useRef(null);
  // Czy gest myszy ZACZAL sie na tle. Bez tego zaznaczanie tekstu w oknie i puszczenie przycisku
  // poza nim zamyka modal razem z wpisanymi danymi — najczestszy blad we wlasnych modalach.
  const gestZTlaRef = useRef(false);
  // `onClose` trzymamy w ref, zeby nasluch Esc rejestrowal sie RAZ na otwarcie. Inaczej strona
  // przekazujaca strzalke inline (a takich jest wiekszosc) odpinalaby i przypinala listener
  // przy kazdym renderze.
  const onCloseRef = useRef(onClose);

  const idInstancji = useId();
  const idTytulu = `${idInstancji}-tytul`;
  const idPodtytulu = `${idInstancji}-podtytul`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Tak samo `initialFocus`: fokus ustawiamy RAZ, przy otwarciu, wiec efekt fokusu czyta go z ref
  // (ten efekt stoi przed nim, wiec przy otwarciu ref jest juz aktualny).
  const initialFocusRef = useRef(initialFocus);
  useEffect(() => {
    initialFocusRef.current = initialFocus;
  }, [initialFocus]);

  const zamknij = useCallback(() => {
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  // Stos otwartych okien: wskazuje, ktory modal jest wierzchni przy obsludze Esc.
  useEffect(() => {
    if (!open) return undefined;
    stosModali.push(idInstancji);
    return () => {
      const i = stosModali.lastIndexOf(idInstancji);
      if (i !== -1) stosModali.splice(i, 1);
    };
  }, [open, idInstancji]);

  useEffect(() => {
    if (!open) return undefined;
    const naKlawisz = (e) => {
      if (e.key !== 'Escape') return;
      if (stosModali[stosModali.length - 1] !== idInstancji) return;
      // Zatrzymujemy zdarzenie, zeby Esc nie zamknal przy okazji panelu pod spodem — jedno
      // nacisniecie ma cofac o JEDEN poziom.
      e.stopPropagation();
      zamknij();
    };
    document.addEventListener('keydown', naKlawisz);
    return () => document.removeEventListener('keydown', naKlawisz);
  }, [open, idInstancji, zamknij]);

  useEffect(() => {
    if (!open) return undefined;
    zablokujPrzewijanieTla();
    return odblokujPrzewijanieTla;
  }, [open]);

  // Fokus: wchodzi do okna po otwarciu i WRACA tam, skad przyszedl, po zamknieciu. Bez powrotu
  // uzytkownik klawiatury po zamknieciu modala laduje na poczatku strony i szuka miejsca od nowa.
  useEffect(() => {
    if (!open) return undefined;
    const poprzednioAktywny = document.activeElement;
    const okno = oknoRef.current;
    if (okno) {
      const wskazany = initialFocusRef.current;
      let cel = null;
      if (wskazany === 'dialog') cel = okno;
      else if (wskazany && wskazany.current) cel = wskazany.current;
      if (!cel) cel = getFocusable(okno)[0] || okno;
      cel.focus();
    }
    return () => {
      if (
        poprzednioAktywny &&
        typeof poprzednioAktywny.focus === 'function' &&
        document.contains(poprzednioAktywny)
      ) {
        poprzednioAktywny.focus();
      }
    };
  }, [open]);

  const naKlawiszWOknie = useCallback((e) => {
    if (e.key !== 'Tab') return;
    const okno = oknoRef.current;
    if (!okno) return;
    const fokusowalne = getFocusable(okno);
    if (fokusowalne.length === 0) {
      // Nie ma dokad przejsc — Tab nie moze wyprowadzic fokusu na strone pod spodem.
      e.preventDefault();
      return;
    }
    const pierwszy = fokusowalne[0];
    const ostatni = fokusowalne[fokusowalne.length - 1];
    const aktywny = document.activeElement;

    if (e.shiftKey && (aktywny === pierwszy || aktywny === okno)) {
      e.preventDefault();
      ostatni.focus();
    } else if (!e.shiftKey && aktywny === ostatni) {
      e.preventDefault();
      pierwszy.focus();
    }
  }, []);

  const naNacisniecieTla = useCallback((e) => {
    gestZTlaRef.current = e.target === e.currentTarget;
  }, []);

  const naKlikniecieTla = useCallback(
    (e) => {
      // Okno stoi w portalu, ale zdarzenia Reacta wedruja po drzewie KOMPONENTOW, nie po DOM:
      // klikniecie w oknie docieralo do przodkow miejsca, w ktorym okno wyrenderowano. Dwie
      // nakladki formularzy (kalendarz sprzetu, szczegoly rezerwacji) zamykaja sie na kazde
      // klikniecie, ktore do nich dotrze, wiec wybor wiersza w oknie zamykal caly formularz.
      // Zatrzymujemy WYLACZNIE klikniecie: `stopPropagation` zatrzymuje tez zdarzenie
      // przegladarki przed `document`, a tam nasluchuje Esc tego okna (klawiszy nie ruszamy).
      e.stopPropagation();
      const zaczetoNaTle = gestZTlaRef.current;
      gestZTlaRef.current = false;
      if (!dismissOnBackdrop) return;
      if (!zaczetoNaTle || e.target !== e.currentTarget) return;
      zamknij();
    },
    [dismissOnBackdrop, zamknij]
  );

  // Zamkniety modal nie montuje portalu w ogole — w `document.body` nie zostaje ukryty wezel,
  // ktory lapalby zdarzenia albo mylil czytnik ekranu.
  if (!open) return null;

  return ReactDOM.createPortal(
    <ModalLayerContext.Provider value={elevated ? WARSTWA_PODNIESIONA : WARSTWA_ZWYKLA}>
    <Tlo
      $elevated={elevated}
      $fullScreenOnMobile={fullScreenOnMobile}
      onMouseDown={naNacisniecieTla}
      onClick={naKlikniecieTla}
      data-modal-backdrop=""
    >
      <Okno
        ref={oknoRef}
        $size={size}
        $elevated={elevated}
        $tall={tall}
        $fullScreenOnMobile={fullScreenOnMobile}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? idTytulu : undefined}
        aria-describedby={subtitle ? idPodtytulu : undefined}
        tabIndex={-1}
        onKeyDown={naKlawiszWOknie}
        data-cy={dataCy}
      >
        <Naglowek $fullScreenOnMobile={fullScreenOnMobile}>
          <Tytuly>
            {title ? <Tytul id={idTytulu}>{title}</Tytul> : null}
            {subtitle ? <Podtytul id={idPodtytulu}>{subtitle}</Podtytul> : null}
          </Tytuly>
          <PrzyciskZamkniecia
            type="button"
            onClick={zamknij}
            aria-label={closeLabel || 'Zamknij'}
            data-cy={dataCy ? `${dataCy}-close` : undefined}
          >
            {/* Znak mnozenia, nie litera x — ma rowne ramiona w kazdym kroju pisma. */}
            <span aria-hidden="true">&times;</span>
          </PrzyciskZamkniecia>
        </Naglowek>

        {toolbar ? (
          <PasekNarzedzi $fullScreenOnMobile={fullScreenOnMobile}>{toolbar}</PasekNarzedzi>
        ) : null}

        {/* Puste ciało (np. ConfirmDialog wywolany samym `title`, bez `message`) nie dostaje
            wlasnego bloku — inaczej padding Tresci zostawial martwa, pusta przerwe miedzy
            naglowkiem a stopka, mimo ze nie bylo czego tam pokazac. */}
        {children ? <Tresc $flush={flush}>{children}</Tresc> : null}

        {footer ? <Stopka $fullScreenOnMobile={fullScreenOnMobile}>{footer}</Stopka> : null}
      </Okno>
    </Tlo>
    </ModalLayerContext.Provider>,
    document.body
  );
}

/* ── Okno potwierdzenia ──────────────────────────────────────────────────────────────────── */

/* Przyciski stopki bierzemy z ui/common.js. Wlasne kopie staly tu przez chwile i byly
   dokladnie tym, co ta fala prac likwiduje: czternastym wariantem przycisku glownego. */

const Komunikat = styled.div`
  margin: 0;
  font-size: var(--fs-base);
  color: var(--text);
  /* Komunikaty przenoszone z window.confirm czesto skladaja sie z dwoch zdan rozdzielonych
     znakiem nowej linii — zachowujemy ten podzial, zwijajac zwykle spacje. */
  white-space: pre-line;
`;

// Poza komponentem, zeby `onClose` nie byl nowa funkcja przy kazdym renderze okna zajetego praca.
const nicNieRob = () => {};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  busy,
  onConfirm,
  onCancel,
}) {
  /* Ton decyduje o KTORYM przycisku z ui/common.js, a nie o kolorze wpisanym tutaj — dzieki temu
     przycisk potwierdzenia w oknie potwierdzenia wyglada dokladnie tak samo jak przycisk tej samej
     rangi na stronie pod spodem. */
  const PrzyciskPotwierdzenia = tone === 'primary' ? PrimaryButton : DangerButton;

  return (
    <Modal
      open={open}
      /* W trakcie pracy (`busy`) okno nie znika ani na Esc, ani po klaknieciu w tlo — akcja juz
         leci na serwer, a zamkniecie sugerowaloby, ze zostala odwolana. */
      onClose={busy ? nicNieRob : onCancel}
      dismissOnBackdrop={!busy}
      title={title || 'Potwierdzenie'}
      size="sm"
      data-cy="confirm-dialog"
      elevated
      footer={
        <>
          <SecondaryButton type="button" onClick={onCancel} disabled={busy} data-cy="confirm-cancel">
            {cancelLabel || 'Anuluj'}
          </SecondaryButton>
          <PrzyciskPotwierdzenia
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-cy="confirm-accept"
          >
            {busy ? 'Proszę czekać…' : confirmLabel || 'Potwierdź'}
          </PrzyciskPotwierdzenia>
        </>
      }
    >
      {message ? <Komunikat>{message}</Komunikat> : null}
    </Modal>
  );
}

/* ── Provider + hak ──────────────────────────────────────────────────────────────────────── */

const ConfirmContext = createContext(null);

let ostrzezonoOBrakuProvidera = false;

/**
 * Siatka bezpieczenstwa na czas migracji stron: strona wyrwana z drzewa providera (albo test
 * montujacy pojedynczy komponent) ma dzialac dalej, a nie wywalac aplikacje na `ctx === null`.
 * Degradacja do `window.confirm` jest brzydka, ale zachowuje SEMANTYKE — o to tu chodzi.
 */
function awaryjnyConfirm(opcje) {
  if (!ostrzezonoOBrakuProvidera) {
    ostrzezonoOBrakuProvidera = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[ui/Modal] useConfirm() wywolane bez <ConfirmProvider>. Uzywam window.confirm — ' +
        'zamontuj provider w App.js.'
    );
  }
  const o = opcje || {};
  const tresc = [o.title, o.message].filter(Boolean).join('\n\n');
  return Promise.resolve(window.confirm(tresc || 'Czy na pewno?'));
}

let licznikZadan = 0;

export function ConfirmProvider({ children }) {
  // Kolejka zyje w ref, a nie w `useState`, z jednego powodu: `resolve` obietnicy MUSI byc wolane
  // POZA aktualizatorem stanu (React w trybie scislym wywoluje aktualizatory dwukrotnie), a zeby
  // wyjac z kolejki pierwsza pozycje w aktualizatorze, trzeba by ja rozstrzygac wlasnie tam.
  // Przerysowanie wymuszamy osobno, licznikiem.
  const kolejkaRef = useRef([]);
  const [, odswiez] = useReducer((n) => n + 1, 0);

  const confirm = useCallback(
    (opcje) =>
      new Promise((resolve) => {
        licznikZadan += 1;
        kolejkaRef.current = [
          ...kolejkaRef.current,
          { id: licznikZadan, opcje: opcje || {}, resolve },
        ];
        odswiez();
      }),
    []
  );

  // Drugie pytanie, ktore przyszlo zanim pierwsze zostalo rozstrzygniete, czeka w kolejce
  // i pokazuje sie zaraz po nim. Zadna obietnica nie ginie.
  const rozstrzygnij = useCallback((wynik) => {
    const [biezaceZadanie, ...reszta] = kolejkaRef.current;
    if (!biezaceZadanie) return;
    kolejkaRef.current = reszta;
    odswiez();
    biezaceZadanie.resolve(wynik);
  }, []);

  const potwierdz = useCallback(() => rozstrzygnij(true), [rozstrzygnij]);
  const anuluj = useCallback(() => rozstrzygnij(false), [rozstrzygnij]);

  // Odmontowanie providera (wylogowanie, przeladowanie drzewa) nie moze zostawic wiszacych
  // obietnic — kazdy oczekujacy `await confirm(...)` dostaje `false`, czyli „nie potwierdzono".
  useEffect(
    () => () => {
      kolejkaRef.current.forEach((z) => z.resolve(false));
      kolejkaRef.current = [];
    },
    []
  );

  const biezace = kolejkaRef.current[0] || null;
  const opcje = biezace ? biezace.opcje : {};

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* `key` po numerze zadania: kolejne pytanie z kolejki montuje sie jako NOWE okno, wiec fokus
          startuje od poczatku, a po zamknieciu wraca na wlasciwy element wywolujacy. */}
      <ConfirmDialog
        key={biezace ? biezace.id : 'brak'}
        open={!!biezace}
        title={opcje.title}
        message={opcje.message}
        confirmLabel={opcje.confirmLabel}
        cancelLabel={opcje.cancelLabel}
        tone={opcje.tone || 'danger'}
        onConfirm={potwierdz}
        onCancel={anuluj}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * Zwraca `confirm(opcje) -> Promise<boolean>`, zeby migracja stron byla mechaniczna:
 *   `if (!window.confirm('...')) return;`  ->  `if (!(await confirm({ message: '...' }))) return;`
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  return useMemo(() => ctx || awaryjnyConfirm, [ctx]);
}

export default Modal;
